import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Search, Users, UserCheck, UserX, Shield, Calendar, Mail, Phone, ChevronLeft, ChevronRight, Clock, User } from "lucide-react";
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import adminApi from '../../../api/adminApi';
import '../../../assets/css/admin/patients.css';

const getInitials = (name = '') =>
  name.split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();

const PatientManagement = () => {
  const location = useLocation();

  const [activeInfoTab, setActiveInfoTab] = useState("personal");
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(location.state?.search || "");

  // Coming from the header's global search — apply the term it passed along
  useEffect(() => {
    if (location.state?.search) {
      setSearchTerm(location.state.search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  const [activeTab, setActiveTab] = useState("All");
  const [sortBy, setSortBy] = useState("Newest");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // ===== FETCH PATIENTS =====
  const fetchPatients = () => {
    setLoading(true);
    adminApi
      .get('/patients')
      .then((res) => setPatients(res.data))
      .catch((err) => {
        console.error('Patients fetch error:', err);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  // ===== FILTER + SORT =====
  let filteredPatients = patients
    .filter(p => activeTab === "All" || p.status === activeTab)
    .filter(p =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

  if (sortBy === "Name") {
    filteredPatients = [...filteredPatients].sort((a, b) => a.name.localeCompare(b.name));
  } else if (sortBy === "Oldest") {
    filteredPatients = [...filteredPatients].reverse();
  }
  // "Newest" is default order (already latest-first from backend)

  const totalPages = Math.ceil(filteredPatients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPatients = filteredPatients.slice(startIndex, startIndex + itemsPerPage);

  const stats = {
    total: patients.length,
    active: patients.filter(p => p.status === "Active").length,
    inactive: patients.filter(p => p.status === "Inactive").length,
    blocked: patients.filter(p => p.status === "Blocked").length
  };

  // ===== STATUS UPDATE (Activate/Deactivate/Block) =====
  const updateStatus = (patient, newStatus, label) => {
    adminApi
      .patch(`/patients/${patient.id}/status`, { status: newStatus.toLowerCase() })
      .then((res) => {
        setPatients(patients.map(p => (p.id === patient.id ? res.data : p)));
        if (selectedPatient?.id === patient.id) setSelectedPatient(res.data);

        Swal.fire({
          title: `${label}!`,
          html: `<strong>${patient.name}</strong> has been ${label.toLowerCase()}.`,
          icon: 'success',
          confirmButtonColor: '#10b981',
          timer: 2500
        });
      })
      .catch((err) => {
        console.error('Status update error:', err);
        Swal.fire({ title: 'Error', text: 'Could not update patient status.', icon: 'error', confirmButtonColor: '#dc2626' });
      });
  };

  const handleActivate = (patient) => {
    Swal.fire({
      title: 'Activate Patient?',
      html: `Are you sure you want to activate <strong>${patient.name}</strong>?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Activate',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#64748b'
    }).then((result) => {
      if (result.isConfirmed) updateStatus(patient, 'Active', 'Activated');
    });
  };

  const handleDeactivate = (patient) => {
    Swal.fire({
      title: 'Deactivate Patient?',
      html: `Are you sure you want to deactivate <strong>${patient.name}</strong>?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Deactivate',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#ff8c42',
      cancelButtonColor: '#64748b'
    }).then((result) => {
      if (result.isConfirmed) updateStatus(patient, 'Inactive', 'Deactivated');
    });
  };

  // ===== DELETE =====
  const handleDelete = (patient) => {
    Swal.fire({
      title: 'Delete Patient?',
      html: `Are you sure you want to permanently delete <strong>${patient.name}</strong>? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b'
    }).then((result) => {
      if (result.isConfirmed) {
        adminApi
          .delete(`/patients/${patient.id}`)
          .then(() => {
            setPatients(patients.filter(p => p.id !== patient.id));
            setSelectedPatient(null);
            Swal.fire({
              title: 'Deleted!',
              html: `<strong>${patient.name}</strong> has been deleted permanently.`,
              icon: 'success',
              confirmButtonColor: '#dc2626',
              timer: 2500
            });
          })
          .catch((err) => {
            console.error('Delete error:', err);
            Swal.fire({ title: 'Error', text: 'Could not delete patient.', icon: 'error', confirmButtonColor: '#dc2626' });
          });
      }
    });
  };

  const handlePageChange = (page) => setCurrentPage(page);

  return (
    <div className="admin-pm-wrapper">
      <div className="admin-pm-container">
        {/* Header */}
        <div className="admin-pm-header">
          <div>
            <h1 className="admin-pm-title">Patient Management</h1>
            <p className="admin-pm-subtitle">Manage patient accounts and information</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="admin-pm-stats-grid">
          <div className="admin-pm-stat-card">
            <div className="admin-pm-stat-icon admin-pm-stat-blue"><Users size={24} /></div>
            <div>
              <div className="admin-pm-stat-value">{stats.total}</div>
              <div className="admin-pm-stat-label">Total Patients</div>
            </div>
          </div>
          <div className="admin-pm-stat-card">
            <div className="admin-pm-stat-icon admin-pm-stat-green"><UserCheck size={24} /></div>
            <div>
              <div className="admin-pm-stat-value">{stats.active}</div>
              <div className="admin-pm-stat-label">Active</div>
            </div>
          </div>
          <div className="admin-pm-stat-card">
            <div className="admin-pm-stat-icon admin-pm-stat-orange"><UserX size={24} /></div>
            <div>
              <div className="admin-pm-stat-value">{stats.inactive}</div>
              <div className="admin-pm-stat-label">Inactive</div>
            </div>
          </div>
          <div className="admin-pm-stat-card">
            <div className="admin-pm-stat-icon admin-pm-stat-red"><Shield size={24} /></div>
            <div>
              <div className="admin-pm-stat-value">{stats.blocked}</div>
              <div className="admin-pm-stat-label">Blocked</div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="admin-pm-main-card">
          {/* Tabs */}
          <div className="admin-pm-tabs">
            {["All", "Active", "Inactive", "Blocked"].map(tab => (
              <button
                key={tab}
                className={`admin-pm-tab ${activeTab === tab ? "admin-pm-tab-active" : ""}`}
                onClick={() => { setActiveTab(tab); setCurrentPage(1); }}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="admin-pm-filters">
            <span className="admin-pm-filter-label">Filter by:</span>
            <select className="admin-pm-filter-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="Newest">Sort by: Newest</option>
              <option value="Oldest">Sort by: Oldest</option>
              <option value="Name">Sort by: Name</option>
            </select>
            <div className="admin-pm-search-wrapper">
              <Search size={18} className="admin-pm-search-icon" />
              <input
                type="text"
                placeholder="Search"
                className="admin-pm-search-input"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              />
            </div>
          </div>

          {/* Content Grid */}
          <div className="admin-pm-content-grid">
            {/* Left: Patient List */}
            <div className="admin-pm-list-section">
              <h3 className="admin-pm-section-title">All Patients</h3>

              {loading ? (
                <div className="admin-pm-empty-state"><p>Loading patients...</p></div>
              ) : filteredPatients.length === 0 ? (
                <div className="admin-pm-empty-state">
                  <Users size={48} />
                  <p>No patients found</p>
                </div>
              ) : (
                <>
                  <div className="admin-pm-table-container">
                    <table className="admin-pm-table">
                      <thead>
                        <tr>
                          <th className="admin-pm-th">Patient Name</th>
                          <th className="admin-pm-th">Status</th>
                          <th className="admin-pm-th">Registered On</th>
                          <th className="admin-pm-th">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedPatients.map(patient => (
                          <tr key={patient.id} className="admin-pm-tr">
                            <td className="admin-pm-td">
                              <div className="admin-pm-patient-info">
                                <div>
                                  <div className="admin-pm-patient-name">{patient.name}</div>
                                  <div className="admin-pm-patient-age">{patient.age}</div>
                                </div>
                              </div>
                            </td>
                            <td className="admin-pm-td">
                              <span className={`admin-pm-status-badge admin-pm-status-${patient.status.toLowerCase()}`}>
                                {patient.status}
                              </span>
                            </td>
                            <td className="admin-pm-td">{patient.registeredOn}</td>
                            <td className="admin-pm-td">
                              <button className="admin-pm-btn-view" onClick={() => setSelectedPatient(patient)}>
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="admin-pm-pagination">
                    <div className="admin-pm-pagination-info">
                      Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredPatients.length)} of {filteredPatients.length} patients
                    </div>
                    <div className="admin-pm-pagination-controls">
                      <button className="admin-pm-pagination-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                        <ChevronLeft size={18} />
                      </button>
                      {[...Array(totalPages)].map((_, index) => {
                        const page = index + 1;
                        if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                          return (
                            <button
                              key={page}
                              className={`admin-pm-pagination-number ${currentPage === page ? "admin-pm-pagination-number-active" : ""}`}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return <span key={page} style={{ padding: '0 8px', color: '#94a3b8' }}>...</span>;
                        }
                        return null;
                      })}
                      <button className="admin-pm-pagination-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Right: Details Panel */}
            <div className="admin-pm-details-panel">
              {selectedPatient ? (
                <div className="admin-pm-details-content">
                  <h2 className="admin-pm-details-title">{selectedPatient.name}</h2>

                  <div className="admin-pm-patient-image-wrapper">
                    <div
                      style={{
                        width: 100, height: 100, borderRadius: '50%',
                        background: '#001F3F', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 32, fontWeight: 700, margin: '0 auto'
                      }}
                    >
                      {getInitials(selectedPatient.name)}
                    </div>
                  </div>

                  {/* Tab Navigation */}
                  <div className="admin-pm-info-tabs">
                    <button
                      className={`admin-pm-info-tab ${activeInfoTab === "personal" ? "admin-pm-info-tab-active" : ""}`}
                      onClick={() => setActiveInfoTab("personal")}
                    >
                      <User size={16} /> Personal Info
                    </button>
                    <button
                      className={`admin-pm-info-tab ${activeInfoTab === "appointments" ? "admin-pm-info-tab-active" : ""}`}
                      onClick={() => setActiveInfoTab("appointments")}
                    >
                      <Clock size={16} /> Appointments
                    </button>
                  </div>

                  {/* Tab Content */}
                  <div className="admin-pm-tab-content">
                    {activeInfoTab === "personal" && (
                      <div>
                        <div className="admin-pm-detail-row">
                          <span className="admin-pm-detail-label">Age:</span>
                          <span className="admin-pm-detail-value">{selectedPatient.age}</span>
                        </div>
                        <div className="admin-pm-detail-row">
                          <span className="admin-pm-detail-label">Gender:</span>
                          <span className="admin-pm-detail-value">{selectedPatient.gender}</span>
                        </div>
                        <div className="admin-pm-detail-row">
                          <span className="admin-pm-detail-label">Country:</span>
                          <span className="admin-pm-detail-value">{selectedPatient.country}</span>
                        </div>
                        <div className="admin-pm-detail-row">
                          <span className="admin-pm-detail-label">Language:</span>
                          <span className="admin-pm-detail-value">{selectedPatient.language}</span>
                        </div>
                        <div className="admin-pm-detail-row">
                          <span className="admin-pm-detail-label"><Mail size={16} /> Email:</span>
                          <span className="admin-pm-detail-value">{selectedPatient.email}</span>
                        </div>
                        <div className="admin-pm-detail-row">
                          <span className="admin-pm-detail-label"><Phone size={16} /> Phone:</span>
                          <span className="admin-pm-detail-value">{selectedPatient.phone}</span>
                        </div>
                        <div className="admin-pm-detail-row">
                          <span className="admin-pm-detail-label"><Calendar size={16} /> Registered:</span>
                          <span className="admin-pm-detail-value">{selectedPatient.registeredOn}</span>
                        </div>
                      </div>
                    )}

                    {activeInfoTab === "appointments" && (
                      <div>
                        {selectedPatient.appointments && selectedPatient.appointments.length > 0 ? (
                          selectedPatient.appointments.map((apt, index) => (
                            <div key={index} className="admin-pm-appointment-item">
                              <div className="admin-pm-appointment-header">
                                <span className="admin-pm-appointment-date">{apt.date}</span>
                                <span className="admin-pm-appointment-status">{apt.status}</span>
                              </div>
                              <div className="admin-pm-appointment-therapist">Therapist: {apt.therapist}</div>
                            </div>
                          ))
                        ) : (
                          <div className="admin-pm-empty-state">
                            <Clock size={48} />
                            <p>No appointments found</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="admin-pm-action-buttons">
                    {selectedPatient.status === "Active" ? (
                      <>
                        <button className="admin-pm-btn admin-pm-btn-deactivate" onClick={() => handleDeactivate(selectedPatient)}>Deactivate</button>
                        <button className="admin-pm-btn admin-pm-btn-delete" onClick={() => handleDelete(selectedPatient)}>Delete</button>
                      </>
                    ) : (
                      <>
                        <button className="admin-pm-btn admin-pm-btn-activate" onClick={() => handleActivate(selectedPatient)}>Activate</button>
                        <button className="admin-pm-btn admin-pm-btn-delete" onClick={() => handleDelete(selectedPatient)}>Delete</button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="admin-pm-no-selection">
                  <Users size={48} />
                  <p>Select a patient to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientManagement;